import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown, ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { pageList } from "@/lib/pagination";

// Generic table with search, filters (first filter = chips, rest = selects),
// sortable columns, and a scrollable body with a sticky header. Pass `pageSize`
// to paginate (page controls render at the bottom); omit it to show all rows.
//
// SERVER MODE. Pass `server` and the table stops filtering, sorting and slicing
// locally — `rows` is treated as the current page exactly as the server sent it,
// and the pager is driven by `server.total`. This exists because client-side
// paging over a capped response is a lie: the filters only ever see the rows that
// happened to fit, and the footer confidently reports a total it can't know.
//
//   server={{
//     total,                                  // true match count, from the server
//     page,                                   // 1-based, owned by the parent
//     loading,                                // shows a subtle busy state
//     onChange({ page, query, filters, sort }) // fetch and update your state
//   }}
//
// The parent owns page/query/filter state in server mode so a refetch can't lose
// it. `pageSize` is still required — it's how the pager works out the page count.
export default function DataTable({
  columns,
  rows,
  searchKeys = [],
  filters = [],
  // Filter values to start on, as { [filterKey]: optionValue } — for arriving
  // from a link that already names the slice ("3 active" on the Overview). The
  // chips render as selected, so the reader can see why the table is narrowed
  // and can clear it; pre-filtering `rows` in the caller instead would leave the
  // controls showing "all" over a subset.
  initialFilters = null,
  onRowClick,
  getRowId = (r) => r.id,
  initialSort = null,
  maxHeight = "30rem",
  emptyLabel = "Nothing here yet.",
  pageSize = null,
  // Opt-in row selection (checkboxes + select-all). When `selectable` is set,
  // the parent owns the selected-id set and gets updates via onSelectionChange.
  // `toolbarExtra` renders a custom bar in the toolbar (e.g. bulk actions).
  selectable = false,
  selectedIds = null,
  onSelectionChange = null,
  toolbarExtra = null,
  // Filter rows to a date window. `dateKey` is the row field holding the
  // timestamp; omit it and no date controls render at all — a range picker over
  // rows with no date would filter everything away and look broken.
  dateKey = null,
  dateLabel = "Date",
  server = null,
}) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(initialFilters || {});
  const [sort, setSort] = useState(initialSort);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState(1);
  // "YYYY-MM-DD" as typed, so an empty string means "open-ended on this side"
  // and one bound alone is a valid filter.
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // Options may be plain strings, {value, label}, or {value, label, count} —
  // normalise once so the chips, the selects and the client-side filter all read
  // the same shape. `count` is optional and, when given, is rendered beside the
  // label ("Failed 43"): the caller supplies it because only the caller knows
  // whether the number describes the whole result set or just the loaded page.
  const normFilters = filters.map((f) => ({
    ...f,
    options: (f.options || []).map((o) =>
      o && typeof o === "object"
        ? { value: o.value, label: o.label ?? o.value, count: o.count, raw: false }
        : { value: o, label: o, count: undefined, raw: true }),
  }));
  // The first filter becomes chips unless it opts out with `as: "select"`. A facet
  // with many long values (audit actions, say) wraps into an unreadable block of
  // pills that is taller than the toolbar it sits in.
  const chipIdx = normFilters.findIndex((f) => f.as !== "select");
  const chipFilter = chipIdx === -1 ? null : normFilters[chipIdx];
  const selectFilters = normFilters.filter((_, i) => i !== chipIdx);

  const filtered = useMemo(() => {
    // In server mode `rows` IS the page — re-filtering it here would hide rows
    // the server already decided should be on this page.
    if (server) return rows;
    let out = [...rows];

    // Every WORD must appear somewhere in the row — not the whole phrase in one
    // field. Searching a test case for "login failed" used to find nothing,
    // because "login" is in the scenario and "failed" is in the run status and
    // no single field held both. Splitting on whitespace makes the natural query
    // work, and still ignores a trailing space (which previously emptied the
    // table as you typed "login ").
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length) {
      out = out.filter((r) => {
        // One joined haystack per row, so a term may match any field and the
        // row is scanned once however many terms were typed.
        const hay = searchKeys.map((k) => String(r[k] ?? "")).join("  ").toLowerCase();
        return terms.every((t) => hay.includes(t));
      });
    }

    filters.forEach((f) => {
      const val = active[f.key];
      if (val && val !== "all") out = out.filter((r) => String(r[f.key]) === val);
    });

    if (dateKey && (from || to)) {
      // Inclusive on both ends, and `to` covers the WHOLE day: a reader picking
      // 1 Jan → 1 Jan means that day, not the single instant at midnight.
      const lo = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
      const hi = to ? new Date(`${to}T23:59:59.999`).getTime() : Infinity;
      out = out.filter((r) => {
        const raw = r[dateKey];
        if (!raw) return false;              // undated rows can't be in a window
        const t = new Date(raw).getTime();
        return Number.isFinite(t) && t >= lo && t <= hi;
      });
    }

    if (sort) {
      const dir = sort.dir === "asc" ? 1 : -1;
      out.sort((a, b) => {
        const av = a[sort.key];
        const bv = b[sort.key];
        if (typeof av === "number" && typeof bv === "number") return dir * (av - bv);
        // Missing values sort to the end in both directions rather than being
        // stringified — String(null) is "null", which sorts between "error" and
        // "passed" and scattered the "not run yet" rows through the middle.
        const aEmpty = av === null || av === undefined || av === "";
        const bEmpty = bv === null || bv === undefined || bv === "";
        if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : (aEmpty ? 1 : -1);
        return dir * String(av).localeCompare(String(bv));
      });
    }
    return out;
  }, [rows, query, active, sort, searchKeys, filters, server, dateKey, from, to]);

  // In server mode the count comes from the database, not from what we received.
  const resultCount = server ? (server.total ?? rows.length) : filtered.length;
  // Is an empty table the result of a search/filter, or is there genuinely nothing?
  // Correct in both modes — in server mode `rows` is already the filtered page.
  const isNarrowed = !!query.trim() || !!from || !!to
    || Object.values(active).some((v) => v && v !== "all");
  const totalPages = pageSize ? Math.max(1, Math.ceil(resultCount / pageSize)) : 1;
  const currentPage = server ? (server.page || 1) : Math.min(page, totalPages);
  const paged = server
    ? rows
    : (pageSize ? filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize) : filtered);

  // Jump back to the first page whenever the result set changes. In server mode
  // the parent owns the page, so tell it instead of resetting behind its back.
  // `from`/`to` belong here as much as the search box: without them, narrowing by
  // date from page 3 left you on the last page of the new, smaller result — which
  // reads as "the filter emptied the table".
  useEffect(() => {
    if (server) return;
    setPage(1);
  }, [query, active, sort, pageSize, server, from, to]);

  // Server mode: debounce the search box so typing doesn't fire a request per
  // keystroke, and always drop back to page 1 when the criteria change.
  const onChangeRef = useRef(server?.onChange);
  onChangeRef.current = server?.onChange;
  const isServer = !!server;
  const activeKey = JSON.stringify(active);
  const sortKey = sort ? `${sort.key}:${sort.dir}` : "";
  const firstRun = useRef(true);
  useEffect(() => {
    if (!isServer) return;
    if (firstRun.current) { firstRun.current = false; return; }
    const t = setTimeout(() => {
      onChangeRef.current?.({
        page: 1, query, filters: JSON.parse(activeKey), sort, from, to,
      });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isServer, query, activeKey, sortKey, from, to]);
  // Keep the manual page-number input in sync with the active page.
  useEffect(() => { setPageInput(currentPage); }, [currentPage]);

  // Commit a manually typed page number (clamped to a valid page).
  function goToPage(value) {
    const n = Math.max(1, Math.min(totalPages, Math.floor(Number(value) || 1)));
    setPageInput(n);
    if (server) server.onChange?.({ page: n, query, filters: active, sort });
    else setPage(n);
  }

  function toggleSort(key) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  }

  function setFilter(key, value) {
    setActive((prev) => ({ ...prev, [key]: value }));
  }

  // --- Selection (opt-in) ---------------------------------------------------
  const selected = useMemo(
    () => (selectedIds instanceof Set ? selectedIds : new Set(selectedIds || [])),
    [selectedIds]
  );
  // Select-all operates on the whole filtered set (all pages), not just the
  // visible page — so a bulk delete acts on everything the filters match.
  const filteredIds = useMemo(() => filtered.map((r) => getRowId(r)), [filtered, getRowId]);
  const allSelected = selectable && filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someSelected = selectable && !allSelected && filteredIds.some((id) => selected.has(id));

  function toggleRow(id) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange?.(next);
  }

  function toggleAll() {
    const next = new Set(selected);
    if (allSelected) filteredIds.forEach((id) => next.delete(id));
    else filteredIds.forEach((id) => next.add(id));
    onSelectionChange?.(next);
  }

  const hasToolbar = searchKeys.length > 0 || filters.length > 0 || !!dateKey
    || (selectable && !!toolbarExtra);
  const DATE_INPUT_CLS =
    "h-10 rounded-xl border border-slate-200 bg-slate-50 px-2.5 text-sm text-slate-700 " +
    "focus:border-brand-400 focus:bg-white focus:outline-none dark:border-white/10 " +
    "dark:bg-white/5 dark:text-slate-200 [color-scheme:light] dark:[color-scheme:dark]";
  const colCount = columns.length + (selectable ? 1 : 0);
  const CHECKBOX_CLS =
    "h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 dark:border-white/20 dark:bg-white/5";

  return (
    <div className="card overflow-hidden">
      {/* toolbar */}
      {hasToolbar && (
      <div className="flex flex-col gap-3 border-b border-slate-200 p-4 dark:border-white/10">
        {selectable && toolbarExtra}
        {/* flex-wrap, not justify-between: with exactly two children (search +
            one select, no date range — Companies' Plan filter, say) `between`
            pinned them to opposite corners with a big dead gap, so the select
            read as misplaced rather than as a filter sitting with the others.
            A flowing, wrapping row keeps every control in one visual group
            regardless of how many are present. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          {searchKeys.length > 0 && (
            <div className="relative w-full sm:max-w-xs">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                // TYPE="TEXT", NOT "SEARCH" — this is what stops the duplicate ✕.
                //
                // `type="search"` makes the browser draw its OWN clear button, and
                // this component already renders one below, so both appeared side
                // by side. Suppressing the native one with
                // `::-webkit-search-cancel-button { display: none }` is a UA-pseudo
                // hack that is not honoured everywhere and had already silently
                // stopped working; a text input has no native control to fight in
                // any browser.
                //
                // The custom button is the one kept because it is themed for light
                // and dark, carries aria-label="Clear search", and goes through
                // setQuery so the table actually re-filters — the native control
                // mutates the value without telling React.
                //
                // role="searchbox" keeps the semantics the type gave assistive tech,
                // and Escape still clears, which is the other affordance
                // type="search" provided.
                type="text"
                role="searchbox"
                // Belt and braces alongside the dialog fix: an unnamed text
                // input next to a password field is exactly what a password
                // manager treats as the username box.
                name="table-search"
                autoComplete="off"
                aria-label="Search this table"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape" && query) { e.preventDefault(); setQuery(""); } }}
                placeholder="Search…"
                className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-8 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:bg-white focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-white/10 dark:hover:text-white"
                >
                  <X size={13} />
                </button>
              )}
            </div>
          )}

          {/* Between two dates. `max`/`min` cross-bind the pair so an end
              before its start can't be picked at all — validating after the
              fact would leave the table empty with no explanation. */}
          {dateKey && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-slate-400">{dateLabel}</span>
              <input
                type="date" value={from} max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
                aria-label={`${dateLabel} from`} className={DATE_INPUT_CLS}
              />
              <span className="text-xs text-slate-400">to</span>
              <input
                type="date" value={to} min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
                aria-label={`${dateLabel} to`} className={DATE_INPUT_CLS}
              />
              {(from || to) && (
                <button
                  type="button"
                  onClick={() => { setFrom(""); setTo(""); }}
                  className="rounded-lg px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Each select is width-capped: a filter value can be long (a page
              URL, say), and an uncapped select stretches the toolbar off the
              screen on anything narrower than a desktop. */}
          {selectFilters.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectFilters.map((f) => (
                <select
                  key={f.key}
                  value={active[f.key] || "all"}
                  onChange={(e) => setFilter(f.key, e.target.value)}
                  className="h-10 max-w-[14rem] truncate rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                >
                  <option value="all">
                    All {f.label}{f.total != null ? ` (${f.total})` : ""}
                  </option>
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}{o.count != null ? ` (${o.count})` : ""}
                    </option>
                  ))}
                </select>
              ))}
            </div>
          )}
        </div>

        {/* chip filter */}
        {chipFilter && (
          <div className="flex flex-wrap gap-1.5">
            {[{ value: "all", label: `All ${chipFilter.label}`, count: chipFilter.total, raw: true },
              ...chipFilter.options].map((opt) => {
              const isActive = (active[chipFilter.key] || "all") === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFilter(chipFilter.key, opt.value)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors",
                    // Only bare values need title-casing; an explicit label is
                    // already written the way it should read.
                    opt.raw && "capitalize",
                    isActive
                      ? "bg-brand-600 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
                  )}
                >
                  {opt.label}
                  {/* How many rows this chip would leave you with, so the
                      choice is informed before it is made rather than after. */}
                  {opt.count != null && (
                    <span
                      className={cn(
                        "rounded-full px-1.5 text-[0.6875rem] font-semibold tabular-nums",
                        isActive
                          ? "bg-white/25 text-white"
                          : "bg-white text-slate-500 dark:bg-white/10 dark:text-slate-300"
                      )}
                    >
                      {opt.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* scrollable table */}
      <div className="overflow-auto" style={{ maxHeight }}>
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur dark:bg-ink-900/95">
            <tr>
              {selectable && (
                <th className="w-10 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    aria-label="Select all rows"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    className={CHECKBOX_CLS}
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    "whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500",
                    col.align === "right" && "text-right",
                    col.align === "center" && "text-center"
                  )}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col.key)}
                      className="inline-flex items-center gap-1 hover:text-slate-800 dark:hover:text-white"
                    >
                      {col.header}
                      {!sort || sort.key !== col.key ? (
                        <ChevronsUpDown size={13} className="opacity-50" />
                      ) : sort.dir === "asc" ? (
                        <ChevronUp size={13} />
                      ) : (
                        <ChevronDown size={13} />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {paged.length === 0 ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="px-4 py-12 text-center text-sm text-slate-400"
                >
                  {/* "No test runs yet" is a lie when a filter simply matched
                      nothing — it sends people hunting for missing data instead of
                      looking at the filter they set. */}
                  {isNarrowed ? "Nothing matches your search or filters." : emptyLabel}
                </td>
              </tr>
            ) : (
              paged.map((row) => {
                const rid = getRowId(row);
                return (
                <tr
                  key={rid}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  // Row click is often the ONLY way into a detail view, so it has to
                  // work from the keyboard. tabIndex + Enter/Space rather than
                  // role="button", which would strip the row/cell semantics screen
                  // readers rely on to navigate a table.
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={onRowClick ? (e) => {
                    if (e.target !== e.currentTarget) return;   // controls inside the row keep their own keys
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();                       // Space must not scroll the page
                      onRowClick(row);
                    }
                  } : undefined}
                  className={cn(
                    "transition-colors",
                    selectable && selected.has(rid) && "bg-brand-50/60 dark:bg-brand-500/10",
                    onRowClick && "cursor-pointer hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/60 dark:hover:bg-white/5"
                  )}
                >
                  {selectable && (
                    <td className="w-10 px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label="Select row"
                        checked={selected.has(rid)}
                        onChange={() => toggleRow(rid)}
                        className={CHECKBOX_CLS}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        "px-4 py-3 text-slate-700 dark:text-slate-300",
                        col.align === "right" && "text-right",
                        col.align === "center" && "text-center",
                        col.className
                      )}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pageSize ? (
        <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-xs text-slate-500 dark:border-white/10 dark:text-slate-400 sm:flex-row">
          <span>
            {paged.length === 0 ? (
              "No results"
            ) : (
              <>
                Showing{" "}
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, resultCount)}
                </span>{" "}
                of {resultCount}
              </>
            )}
          </span>
          {totalPages > 1 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              {/* manual page jump */}
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400">Go to</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); goToPage(e.currentTarget.value); } }}
                  onBlur={(e) => goToPage(e.currentTarget.value)}
                  aria-label="Go to page"
                  className="h-8 w-14 rounded-lg border border-slate-200 bg-white px-2 text-center text-xs font-medium text-slate-700 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                />
                <span className="text-slate-400">/ {totalPages}</span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
              >
                <ChevronLeft size={15} />
              </button>
              {pageList(currentPage, totalPages).map((p, i) =>
                p === "…" ? (
                  <span key={`gap-${i}`} className="px-1.5 text-slate-400">…</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => goToPage(p)}
                    className={cn(
                      "h-8 min-w-8 rounded-lg px-2 text-xs font-semibold transition-colors",
                      p === currentPage
                        ? "bg-brand-600 text-white shadow-soft"
                        : "border border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                    )}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
              >
                <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Unpaged: `resultCount` IS `filtered.length` in client mode, so this read
        // "Showing 12 of 12" forever. Compare against the unfiltered input, which
        // is the number the user actually wants when a filter is on.
        <div className="border-t border-slate-200 px-4 py-2.5 text-xs text-slate-400 dark:border-white/10">
          {isNarrowed
            ? `Showing ${paged.length} of ${rows.length}`
            : `${resultCount} ${resultCount === 1 ? "row" : "rows"}`}
        </div>
      )}
    </div>
  );
}
